import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { Button } from '~/components/ui/Button';
import { ThemeToggle } from '~/components/ui/ThemeToggle';
import { clsx } from 'clsx';
import { WrapperWidget } from '@agentselly/ui';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useForm, type FormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { fetchBackend } from '~/helper/fetchBackend';
import { toast } from 'sonner';
import {
  ActionSchema,
  AppAction,
  AppRoute,
  HomeLivingSituationEnums,
  RealEstateWishesEnums,
  SalutationEnums,
  type ProfileType,
} from '@agentselly/immodossier-shared';
import { getEnumValues } from '~/shared/getEnumValues';
import { formatPhoneNumber } from '~/shared/formatPhoneNumber';
import { useAppParams } from '~/helper/useAppParams';
import { Route as ProfileRoute } from '~/routes/$lang.$companyId/$contactId/profile';
import {
  CheckCircleIcon,
  InformationCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

interface Props {
  readonly contactId: string;
  readonly contact: {
    readonly salutation: string;
    readonly firstname: string;
    readonly lastname: string;
    readonly email?: string;
    readonly phone?: string;
    readonly mobilephone?: string;
    readonly current_living_situation?: string;
    readonly real_estate_wishes?: string;
    readonly knows_us_from?: string;
  };
  readonly v3Redesign?: boolean;
}

export function ProfileFormWidget(props: Props) {
  if (!props.v3Redesign) {
    return <LegacyProfileFormWidget {...props} />;
  }

  return <V3ProfileFormWidget {...props} />;
}

interface CollapsibleSectionProps {
  readonly title: string;
  readonly children: React.ReactNode;
  readonly defaultOpen?: boolean;
}

function CollapsibleSection({
  title,
  children,
  defaultOpen = true,
}: CollapsibleSectionProps) {
  const [
    isOpen,
    setIsOpen,
  ] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-[#27272a] rounded-lg overflow-hidden bg-white dark:bg-[#18181b]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-[#27272a] transition-colors"
      >
        <h3 className="font-semibold text-base px-1 text-gray-900 dark:text-[#f4f4f5]">
          {title}
        </h3>
        <ChevronDownIcon
          className={clsx(
            'w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform',
            { 'rotate-180': isOpen },
          )}
        />
      </button>
      {isOpen && <div className="p-3 pt-0">{children}</div>}
    </div>
  );
}

function LegacyProfileFormWidget(props: Props) {
  const { t } = useTranslate();
  const params = useAppParams(ProfileRoute);
  const queryClient = useQueryClient();
  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const defaultValues = Object.fromEntries(
    Object.entries(props.contact || {}).map(
      ([
        key,
        value,
      ]) => [
        key,
        value ?? '',
      ],
    ),
  );

  const form = useForm({
    defaultValues: defaultValues,
    resolver: zodResolver(ActionSchema[AppAction.ProfileForm] as any),
  });

  if (!props.contactId) {
    return (
      <WrapperWidget>
        <div className="text-center p-4">
          <p className="text-error">Fehler: Contact ID fehlt</p>
        </div>
      </WrapperWidget>
    );
  }

  const salutationOptions = getEnumValues(SalutationEnums as any);
  const livingSituationOptions = getEnumValues(HomeLivingSituationEnums as any);
  const realEstateWishesOptions = getEnumValues(RealEstateWishesEnums as any);

  const handlePhoneInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    form.setValue('phone', formatPhoneNumber(event.target.value));
  };

  const handleMobileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    form.setValue('mobilephone', formatPhoneNumber(event.target.value));
  };

  const onSubmit = async (data: any) => {
    if (!props.contactId) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      for (const [
        key,
        value,
      ] of Object.entries(data || {})) {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      }

      const response = await fetchBackend(
        `/action/${AppAction.ProfileForm}?contactId=${props.contactId}`,
        { method: 'POST', headers: {}, body: formData },
      );

      if (!response.ok) {
        throw new Error(
          `FormSubmit: ${response.status} ${response.statusText}`,
        );
      }

      const zodResult = await response.json();

      if (zodResult.success) {
        form.reset(data);
        toast.success(t('profile_form_saved'));
        await queryClient.invalidateQueries({
          queryKey: [
            AppRoute.$LANG_$COMPANYID_$CONTACTID_PROFILE,
            params,
          ],
        });
      } else {
        for (const [
          key,
          errors,
        ] of Object.entries(zodResult.errors || {})) {
          form.setError(key as any, {
            type: 'server',
            message: (errors as string[]).join(', '),
          });
        }
        toast.error(t('profile_form_not_saved'));
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(t('profile_form_not_saved'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderRadioGroup = (name: 'salutation', options: string[]) => {
    const selectedValue = form.watch(name);
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map((option, idx) => {
          const isSelected = selectedValue === option;
          return (
            <label
              key={idx}
              className={clsx(
                'relative flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all duration-200 ease-in-out',
                'hover:shadow-sm dark:hover:shadow-gray-900/50 hover:-translate-y-0.5',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm dark:shadow-gray-900/50'
                  : 'border-gray-200 dark:border-[#27272a] bg-white dark:bg-[#18181b] hover:border-gray-300 dark:hover:border-[#3f3f46]',
              )}
            >
              <input
                type="radio"
                {...form.register(name)}
                value={option}
                checked={isSelected}
                className="sr-only"
              />
              <span
                className={clsx(
                  'text-xs font-medium text-center leading-tight',
                  isSelected
                    ? 'text-primary'
                    : 'text-gray-700 dark:text-[#e4e4e7]',
                )}
              >
                {t('profile_form_widget_' + option)}
              </span>
              {isSelected && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary" />
              )}
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <WrapperWidget>
      <div className="h-full flex flex-col gap-y-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-[#f4f4f5]">
            {t('profile_form_widget_title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-[#a1a1aa]">
            {t('profile_form_widget_text')}
          </p>
        </div>

        <form
          method="post"
          onSubmit={form.handleSubmit(onSubmit)}
          className={clsx('flex flex-col gap-3 w-full', {
            'animate-pulse pointer-events-none': isSubmitting,
          })}
        >
          <CollapsibleSection title={t('profile_form_section_personal_info')}>
            <div className="flex flex-col gap-4 pt-3">
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                    {t('profile_form_widget_salutation')}
                  </span>
                </label>
                {renderRadioGroup('salutation', salutationOptions)}
                <LegacyError
                  name="salutation"
                  formState={form.formState}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                      {t('profile_form_widget_firstname')}
                    </span>
                  </label>
                  <input
                    {...form.register('firstname')}
                    placeholder={t('profile_form_widget_firstname_placeholder')}
                    className="input input-bordered w-full"
                  />
                  <LegacyError
                    name="firstname"
                    formState={form.formState}
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                      {t('profile_form_widget_lastname')}
                    </span>
                  </label>
                  <input
                    {...form.register('lastname')}
                    placeholder={t('profile_form_widget_lastname_placeholder')}
                    className="input input-bordered w-full"
                  />
                  <LegacyError
                    name="lastname"
                    formState={form.formState}
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title={t('profile_form_section_contact_info')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                    {t('profile_form_widget_phone')}
                  </span>
                </label>
                <input
                  {...form.register('phone')}
                  placeholder={t('profile_form_widget_phone_placeholder')}
                  onChange={handlePhoneInputChange}
                  className="input input-bordered w-full"
                />
                <LegacyError
                  name="phone"
                  formState={form.formState}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                    {t('profile_form_widget_mobile')}
                  </span>
                </label>
                <input
                  {...form.register('mobilephone')}
                  placeholder={t('profile_form_widget_mobile_placeholder')}
                  onChange={handleMobileInputChange}
                  className="input input-bordered w-full"
                />
                <LegacyError
                  name="mobilephone"
                  formState={form.formState}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title={t('profile_form_section_preferences')}>
            <div className="flex flex-col gap-4 pt-3">
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                    {t('profile_form_widget_home_living_situation')}
                  </span>
                </label>
                <select
                  {...form.register('current_living_situation')}
                  className="select select-bordered w-full"
                >
                  <option value="">{t('profile_form_please_choose')}</option>
                  {livingSituationOptions.map((option, idx) => (
                    <option
                      key={idx}
                      value={option}
                    >
                      {t('profile_form_widget_home_living_situation_' + option)}
                    </option>
                  ))}
                </select>
                <LegacyError
                  name="current_living_situation"
                  formState={form.formState}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text text-gray-700 dark:text-[#e4e4e7]">
                    {t('profile_form_widget_real_estate_wishes')}
                  </span>
                </label>
                <select
                  {...form.register('real_estate_wishes')}
                  className="select select-bordered w-full"
                >
                  <option value="">{t('profile_form_please_choose')}</option>
                  {realEstateWishesOptions.map((option, idx) => (
                    <option
                      key={idx}
                      value={option}
                    >
                      {t('profile_form_widget_real_estate_wishes_' + option)}
                    </option>
                  ))}
                </select>
                <LegacyError
                  name="real_estate_wishes"
                  formState={form.formState}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t('profile_appearance_title', 'Erscheinungsbild')}
          >
            <div className="pt-3 space-y-3">
              <p className="text-xs text-gray-500 dark:text-[#a1a1aa]">
                {t(
                  'profile_appearance_desc',
                  'Wähle zwischen hellem, dunklem oder dem System-Design.',
                )}
              </p>
              <ThemeToggle />
            </div>
          </CollapsibleSection>

          <div className="pt-2">
            <Button
              type="submit"
              primary={true}
              isLoading={isSubmitting}
              showArrow
              className="md:w-auto"
            >
              {t('save')}
            </Button>
          </div>
        </form>

        {props.contact.email && (
          <div className="pt-2 border-t border-gray-200 dark:border-[#27272a]">
            <p className="text-sm text-gray-500 dark:text-[#a1a1aa]">
              <i>
                {t('profile_form_widget_email_text', {
                  email: props.contact.email,
                })}
              </i>
            </p>
          </div>
        )}
      </div>
    </WrapperWidget>
  );
}

function LegacyError(props: {
  readonly name: string;
  readonly formState: any;
}) {
  const errorMessage = props?.formState?.errors?.[props.name]?.message || '';
  if (!errorMessage) return null;
  return (
    <p className="label-text-alt text-error pt-1">{String(errorMessage)}</p>
  );
}

function V3ProfileFormWidget(props: Props) {
  const { t } = useTranslate();
  const params = useAppParams(ProfileRoute);
  const queryClient = useQueryClient();
  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);
  const [
    saveSuccess,
    setSaveSuccess,
  ] = useState(false);

  const defaultValues = Object.fromEntries(
    Object.entries(props.contact || {}).map(
      ([
        key,
        value,
      ]) => [
        key,
        value ?? '',
      ],
    ),
  );

  const form = useForm<ProfileType>({
    defaultValues: defaultValues as unknown as ProfileType,
    resolver: zodResolver(ActionSchema[AppAction.ProfileForm]),
  });

  if (!props.contactId) {
    return (
      <div className="v3-card p-6 text-center">
        <p className="v3-ferr">
          {t('profile_form_missing_id', 'Fehler: Contact ID fehlt')}
        </p>
      </div>
    );
  }

  const salutationOptions = getEnumValues(SalutationEnums);
  const livingSituationOptions = getEnumValues(HomeLivingSituationEnums);
  const realEstateWishesOptions = getEnumValues(RealEstateWishesEnums);

  const handlePhoneInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    form.setValue('phone', formatPhoneNumber(event.target.value));
  };

  const handleMobileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    form.setValue('mobilephone', formatPhoneNumber(event.target.value));
  };

  const isProfileEmpty =
    !props.contact?.firstname &&
    !props.contact?.lastname &&
    !props.contact?.email;

  const onSubmit = useCallback(
    async (data: ProfileType) => {
      if (!props.contactId) {
        return;
      }

      setIsSubmitting(true);
      setSaveSuccess(false);

      try {
        const formData = new FormData();
        for (const [
          key,
          value,
        ] of Object.entries(data || {})) {
          if (value !== undefined && value !== null) {
            formData.append(key, String(value));
          }
        }

        const response = await fetchBackend(
          `/action/${AppAction.ProfileForm}?contactId=${props.contactId}`,
          { method: 'POST', headers: {}, body: formData },
        );

        if (!response.ok) {
          throw new Error(
            `FormSubmit: ${response.status} ${response.statusText}`,
          );
        }

        const zodResult = (await response.json()) as {
          success: boolean;
          errors?: Record<string, string[]>;
        };

        if (zodResult.success) {
          form.reset(data);
          setSaveSuccess(true);
          toast.success(
            t('profile_form_saved', 'Profil erfolgreich gespeichert.'),
          );
          await queryClient.invalidateQueries({
            queryKey: [
              AppRoute.$LANG_$COMPANYID_$CONTACTID_PROFILE,
              params,
            ],
          });
        } else {
          for (const [
            key,
            errors,
          ] of Object.entries(zodResult.errors || {})) {
            form.setError(key as keyof ProfileType, {
              type: 'server',
              message: (errors || []).join(', '),
            });
          }
          toast.error(
            t(
              'profile_form_not_saved',
              'Profil konnte nicht gespeichert werden.',
            ),
          );
        }
      } catch (error) {
        console.error('Submission error:', error);
        toast.error(
          t(
            'profile_form_not_saved',
            'Profil konnte nicht gespeichert werden.',
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      form,
      props.contactId,
      t,
      params,
      queryClient,
    ],
  );

  return (
    <form
      id="pf"
      noValidate
      data-od-id="profile-form"
      aria-labelledby="h-pf"
      onSubmit={form.handleSubmit(onSubmit)}
      className="v3-card h-full justify-between"
    >
      <div className="v3-stack">
        <div className="v3-head">
          <h2
            id="h-pf"
            className="text-lg font-bold text-[var(--v3-tx)]"
          >
            {t('profile_form_widget_title', 'Persönliche Angaben')}
          </h2>
          <p className="text-sm text-[var(--v3-mu)]">
            {t(
              'profile_form_widget_text',
              'Damit sprechen wir Sie richtig an und erreichen Sie.',
            )}
          </p>
        </div>

        {isProfileEmpty && (
          <div
            className="v3-callout"
            role="status"
          >
            <InformationCircleIcon className="w-5 h-5 flex-none text-[var(--v3-mu)]" />
            <div className="text-xs">
              <b>{t('profile_empty_title', 'Ihr Profil ist noch leer.')}</b>{' '}
              {t(
                'profile_empty_desc',
                'Mit Ihren Angaben können wir Sie persönlich ansprechen und Ihr Dossier optimal vorbereiten.',
              )}
            </div>
          </div>
        )}

        <div className="v3-fgrid two">
          <div className="v3-fld">
            <label htmlFor="p-sal">
              {t('profile_form_widget_salutation', 'Anrede')}
            </label>
            <select
              id="p-sal"
              className="v3-inp"
              {...form.register('salutation')}
            >
              <option value="">
                {t('profile_form_please_choose', 'Bitte wählen')}
              </option>
              {salutationOptions.map((opt) => (
                <option
                  key={opt}
                  value={opt}
                >
                  {t(
                    `profile_form_widget_${opt}`,
                    opt === 'female' ? 'Frau' : 'Herr',
                  )}
                </option>
              ))}
            </select>
            <FieldError
              name="salutation"
              formState={form.formState}
            />
          </div>

          <div className="v3-fld">
            <label htmlFor="p-liv">
              {t('profile_form_widget_home_living_situation', 'Wohnsituation')}
            </label>
            <select
              id="p-liv"
              className="v3-inp"
              {...form.register('current_living_situation')}
            >
              <option value="">
                {t('profile_form_please_choose', 'Bitte wählen')}
              </option>
              {livingSituationOptions.map((opt) => (
                <option
                  key={opt}
                  value={opt}
                >
                  {t(`profile_form_widget_home_living_situation_${opt}`, opt)}
                </option>
              ))}
            </select>
            <FieldError
              name="current_living_situation"
              formState={form.formState}
            />
          </div>

          <div className="v3-fld">
            <label htmlFor="p-fn">
              {t('profile_form_widget_firstname', 'Vorname')}
            </label>
            <input
              id="p-fn"
              className="v3-inp"
              autoComplete="given-name"
              placeholder={t(
                'profile_form_widget_firstname_placeholder',
                'Vorname',
              )}
              {...form.register('firstname')}
            />
            <FieldError
              name="firstname"
              formState={form.formState}
            />
          </div>

          <div className="v3-fld">
            <label htmlFor="p-ln">
              {t('profile_form_widget_lastname', 'Nachname')}
            </label>
            <input
              id="p-ln"
              className="v3-inp"
              autoComplete="family-name"
              placeholder={t(
                'profile_form_widget_lastname_placeholder',
                'Nachname',
              )}
              {...form.register('lastname')}
            />
            <FieldError
              name="lastname"
              formState={form.formState}
            />
          </div>

          <div className="v3-fld">
            <label htmlFor="p-ph">
              {t('profile_form_widget_phone', 'Telefon')}
            </label>
            <input
              id="p-ph"
              type="tel"
              className="v3-inp"
              autoComplete="tel"
              placeholder={t(
                'profile_form_widget_phone_placeholder',
                '+41 44 123 45 67',
              )}
              {...form.register('phone')}
              onChange={handlePhoneInputChange}
            />
            <FieldError
              name="phone"
              formState={form.formState}
            />
          </div>

          <div className="v3-fld">
            <label htmlFor="p-mo">
              {t('profile_form_widget_mobile', 'Mobile')}
            </label>
            <input
              id="p-mo"
              type="tel"
              className="v3-inp"
              autoComplete="tel"
              placeholder={t(
                'profile_form_widget_mobile_placeholder',
                '+41 79 123 45 67',
              )}
              {...form.register('mobilephone')}
              onChange={handleMobileInputChange}
            />
            <FieldError
              name="mobilephone"
              formState={form.formState}
            />
          </div>
        </div>

        <div className="v3-fld">
          <label htmlFor="p-wish">
            {t('profile_form_widget_real_estate_wishes', 'Immobilienwunsch')}
          </label>
          <select
            id="p-wish"
            className="v3-inp"
            {...form.register('real_estate_wishes')}
          >
            <option value="">
              {t('profile_form_please_choose', 'Bitte wählen')}
            </option>
            {realEstateWishesOptions.map((opt) => (
              <option
                key={opt}
                value={opt}
              >
                {t(`profile_form_widget_real_estate_wishes_${opt}`, opt)}
              </option>
            ))}
          </select>
          <FieldError
            name="real_estate_wishes"
            formState={form.formState}
          />
        </div>

        <div className="v3-fld">
          <span
            className="v3-lbl"
            id="p-th"
          >
            {t('profile_appearance_title', 'Erscheinungsbild')}
          </span>
          <p className="text-xs text-[var(--v3-mu)] -mt-1">
            {t(
              'profile_appearance_desc',
              'Wählen Sie zwischen hellem, dunklem oder dem System-Design.',
            )}
          </p>
          <ThemeToggle />
        </div>

        <div className="v3-fbar">
          <button
            type="submit"
            disabled={isSubmitting}
            className="v3-btn pri"
          >
            {isSubmitting
              ? t('saving', 'Wird gespeichert...')
              : t('save', 'Speichern')}
          </button>
          <span className="v3-src">
            <UserGroupIcon className="w-4 h-4 text-[var(--v3-mu)] flex-none" />
            {t('profile_form_hubspot_sync', 'Wird mit HubSpot synchronisiert')}
          </span>
        </div>

        {saveSuccess && (
          <div
            className="v3-okbox"
            id="pf-saved"
            role="status"
          >
            <CheckCircleIcon className="w-5 h-5 flex-none" />
            <span>{t('profile_form_saved', 'Profil gespeichert.')}</span>
          </div>
        )}

        {props.contact.email && (
          <div className="pt-2 border-t border-[var(--v3-bd)]">
            <p className="text-xs text-[var(--v3-mu)]">
              <i>
                {t('profile_form_widget_email_text', {
                  email: props.contact.email,
                })}
              </i>
            </p>
          </div>
        )}
      </div>
    </form>
  );
}

interface FieldErrorProps {
  readonly name: string;
  readonly formState: FormState<ProfileType>;
}

function FieldError(props: FieldErrorProps) {
  const errors = props.formState.errors as Record<
    string,
    { message?: string } | undefined
  >;
  const errorMessage = errors[props.name]?.message;
  if (!errorMessage) return null;
  return <p className="v3-ferr">{String(errorMessage)}</p>;
}
